// MyPage — 내 정보(프로필·비번변경·2FA·알림·메일 AI 지침·접속로그). 다우 캘린더는 이메일 연동 시 자동 미러(별도 메뉴 폐지).
// frontend/src/pages/MyPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, chatAPI, pushAPI } from '../api/client';
import AppShell from '../components/AppShell';
import useIsMobile from '../hooks/useIsMobile';
import { Page, PageHeader, Button, LoadingState } from '../components/ui';
import { IconUser } from '@tabler/icons-react';
import { card as cardBase, input as inputBase, RADIUS } from '../utils/uiStyles';
import FontPicker from '../components/FontPicker';
import MailAiGuideCard from '../components/MailAiGuideCard';
import { notePushSubscriptionChanged } from '../messengerOnScreen';
import { swReady } from '../utils/push';

// 백엔드는 UTC(시간대 표기 없음)로 저장 → UTC로 간주해 KST 날짜로 표시
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

// PC 를 쓰는 중일 때 폰 메신저 알림 지연(초). 선택지는 서버가 준 choices 를 쓰고, 없으면 이 값.
const MOBILE_DELAY_FALLBACK = [0, 60, 180, 300, 600];
const toDelaySec = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

// 구독을 바꿨다고 메신저 알림 억제(messengerOnScreen)에 알린다 — 다시 읽게 할 뿐 값은 넘기지 않는다(push 유틸 파일과 같은 가드).
// 🔴 시험 대역이 그 export 를 안 내보낼 수 있고(SplitShell.test) 알림 실패가 화면을 깨면 안 된다 → 확인·삼킨다.
function notePushChanged() {
  try {
    if (typeof notePushSubscriptionChanged !== 'function') return;
    const p = notePushSubscriptionChanged();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) { /* */ }
}
const delayLabel = (sec, def) => {
  const base = sec === 0 ? '바로' : (sec % 60 === 0 ? `${sec / 60}분 뒤` : `${sec}초 뒤`);
  return sec === def ? `${base} (기본)` : base;
};

// ── 공통 UI 컴포넌트 (함수 외부에 정의 → 리렌더 시 재마운트 없음) ──
function Card({ children, title, compact }) {
  return (
    <div style={{ ...cardBase, marginBottom: compact ? '14px' : '16px', boxSizing: 'border-box' }}>
      <h2 style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)', margin: '0 0 14px' }}>{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', color: 'var(--gw-secondary, #64635f)', fontWeight: '500', marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  );
}

// 맨 <input> 하나만 그린다 — 감싸면 이름 줄(closest('div'))이 바뀐다(MyPage.test)
function Input(props) {
  return (
    <input {...props} style={{ ...inputBase, minHeight: '31px', minWidth: 0, ...props.style }} />
  );
}

export default function MyPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [profile,  setProfile]  = useState(null);
  const [usage,    setUsage]    = useState({ used: 0, limit: 0 });
  const [nameEdit, setNameEdit] = useState('');
  const [nameMode, setNameMode] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg,    setPwMsg]    = useState(null); // { ok, text }
  const [nameMsg,  setNameMsg]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [pushMsg,  setPushMsg]  = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushOn,   setPushOn]   = useState(false);  // 이 브라우저의 실제 구독 상태
  // 폰 메신저 알림 지연 { value, choices, def } — 못 받으면(옛 서버·pushAPI 에 getPrefs 없음) null = 선택 상자를 숨긴다.
  const [delayPrefs, setDelayPrefs] = useState(null);
  const [delayBusy,  setDelayBusy]  = useState(false);
  const [delayMsg,   setDelayMsg]   = useState(null);

  useEffect(() => {
    authAPI.getMe().then((r) => {
      setProfile(r.data);
      setNameEdit(r.data.name);
    }).catch(() => navigate('/'));
    chatAPI.getMyUsage().then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pushAPI || typeof pushAPI.getPrefs !== 'function') return undefined;
    let dead = false;
    Promise.resolve()
      .then(() => pushAPI.getPrefs())
      .then((r) => {
        const d = r && r.data;
        const value = toDelaySec(d && d.mobile_delay_sec);
        if (dead || value === null) return;
        const fromServer = Array.isArray(d.choices) ? d.choices.map(toDelaySec).filter((v) => v !== null) : [];
        const choices = fromServer.length ? fromServer : MOBILE_DELAY_FALLBACK;
        setDelayPrefs({
          value,
          choices: choices.includes(value) ? choices : [...choices, value].sort((a, b) => a - b),
          def: toDelaySec(d.default),
        });
      })
      .catch(() => { /* 못 받으면 선택 상자를 숨긴다 */ });
    return () => { dead = true; };
  }, []);

  // 푸시 관련(모든 Hook은 early return 위에 있어야 함).
  const urlB64ToUint8 = (s) => {
    const pad = '='.repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  };
  const pushSupported = ('serviceWorker' in navigator) && ('PushManager' in window);
  // swReady 는 utils/push 것을 쓴다 — 6초 시한(null) + 루트(/) 등록만 돌려준다(루트 등록 판정 공유 · #3140).
  const doSubscribe = async () => {
    try {
      const reg = await swReady();
      if (!reg) throw new Error('SW_UNAVAILABLE');
      const { data } = await pushAPI.getVapidKey();
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(data.public_key) });
      await pushAPI.subscribe(sub.toJSON());
    } finally {
      notePushChanged();   // 성공·실패 무관 — 마운트 재구독·알림 켜기 둘 다 여기를 지난다
    }
  };
  useEffect(() => {
    if (!pushSupported || !profile) return undefined;
    let dead = false;
    swReady().then(async (reg) => {
      if (!reg) { if (!dead) setPushOn(false); return; }
      const sub = await reg.pushManager.getSubscription();
      if (sub) { if (!dead) setPushOn(true); return; }
      if (profile.notifications_enabled && Notification.permission === 'granted') {
        try { await doSubscribe(); if (!dead) setPushOn(true); } catch (e) { if (!dead) setPushOn(false); }
      } else if (!dead) setPushOn(false);
    }).catch(() => {});
    return () => { dead = true; };
  }, [profile]);

  const saveName = async () => {
    if (!nameEdit.trim()) return;
    try {
      await authAPI.updateMe({ name: nameEdit.trim() });
      setProfile((p) => ({ ...p, name: nameEdit.trim() }));
      // localStorage user 갱신
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, name: nameEdit.trim() }));
      setNameMode(false);
      setNameMsg({ ok: true, text: '이름이 변경됐습니다.' });
      setTimeout(() => setNameMsg(null), 3000);
    } catch (e) {
      setNameMsg({ ok: false, text: e.response?.data?.detail || '변경 실패' });
    }
  };

  const changePassword = async () => {
    if (!pw.current || !pw.next || !pw.confirm) {
      setPwMsg({ ok: false, text: '모든 항목을 입력하세요.' }); return;
    }
    if (pw.next !== pw.confirm) {
      setPwMsg({ ok: false, text: '새 비밀번호가 일치하지 않습니다.' }); return;
    }
    if (pw.next.length < 8) {
      setPwMsg({ ok: false, text: '비밀번호는 8자 이상이어야 합니다.' }); return;
    }
    setLoading(true);
    try {
      const r = await authAPI.changePassword({ current_password: pw.current, new_password: pw.next });
      // 비번 변경 시 기존 토큰은 무효화되므로(G11) 서버가 준 새 토큰으로 현재 세션 유지
      if (r.data?.access_token) localStorage.setItem('token', r.data.access_token);
      setPw({ current: '', next: '', confirm: '' });
      setPwMsg({ ok: true, text: '비밀번호가 변경됐습니다.' });
    } catch (e) {
      setPwMsg({ ok: false, text: e.response?.data?.detail || '변경 실패' });
    } finally {
      setLoading(false);
      setTimeout(() => setPwMsg(null), 4000);
    }
  };

  if (!profile) return (
    <AppShell app="my" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <Page>
          <PageHeader icon={IconUser} tone="orange" title="내 정보" />
          <div style={{ maxWidth: '540px' }}>
            <div style={{ ...cardBase, boxSizing: 'border-box' }}>
              <LoadingState>내 정보를 불러오는 중...</LoadingState>
            </div>
          </div>
        </Page>
      </div>
    </AppShell>
  );

  const limit     = usage.limit || 0;
  const used      = usage.used || 0;
  const myUsed    = usage.my_used != null ? usage.my_used : used;
  const groupUsed = usage.group_used || 0;
  const isShared  = usage.scope === 'shared';
  const pct       = limit > 0 ? Math.round((used / limit) * 100) : 0;
  const groupPct  = limit > 0 ? Math.round((groupUsed / limit) * 100) : 0;
  const myPct     = Math.max(pct - groupPct, 0);
  const barColor  = pct >= 100 ? 'var(--gw-red, #d66b60)' : pct >= 80 ? 'var(--gw-yellow, #c99630)' : 'var(--gw-blue, #2383e2)';
  const myW       = limit > 0 ? Math.min((myUsed / limit) * 100, 100) : 0;
  const grW       = limit > 0 ? Math.min((groupUsed / limit) * 100, Math.max(0, 100 - myW)) : 0;

  const wsLimit     = usage.web_search_limit || 0;
  const wsUsed      = usage.web_search_used || 0;
  const wsMyUsed    = usage.web_search_my_used != null ? usage.web_search_my_used : wsUsed;
  const wsGroupUsed = usage.web_search_group_used || 0;
  const wsShared    = usage.web_search_scope === 'shared';
  const wsPct       = wsLimit > 0 ? Math.round((wsUsed / wsLimit) * 100) : 0;
  const wsGroupPct  = wsLimit > 0 ? Math.round((wsGroupUsed / wsLimit) * 100) : 0;
  const wsMyPct     = Math.max(wsPct - wsGroupPct, 0);
  const wsColor     = wsPct >= 100 ? 'var(--gw-red, #d66b60)' : wsPct >= 80 ? 'var(--gw-yellow, #c99630)' : 'var(--gw-green, #539b77)';
  const wsMyW       = wsLimit > 0 ? Math.min((wsMyUsed / wsLimit) * 100, 100) : 0;
  const wsGrW       = wsLimit > 0 ? Math.min((wsGroupUsed / wsLimit) * 100, Math.max(0, 100 - wsMyW)) : 0;

  const enablePush = async () => {
    setPushMsg(null);
    if (!pushSupported) { setPushMsg({ ok: false, text: '이 브라우저는 푸시 알림을 지원하지 않습니다.' }); return; }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushMsg({ ok: false, text: '알림 권한이 거부되었습니다. 브라우저 설정에서 허용해 주세요.' }); return; }
      await doSubscribe();
      setPushOn(true);
      setPushMsg({ ok: true, text: '알림이 켜졌습니다.' });
    } catch (e) {
      const text = e.message === 'SW_UNAVAILABLE'
        ? '이 환경에선 푸시 알림을 사용할 수 없습니다(인증서/브라우저 미지원).'
        : '알림 설정 실패: ' + (e.message || e);
      setPushMsg({ ok: false, text });
    } finally { setPushBusy(false); }
  };
  const disablePush = async () => {
    setPushMsg(null); setPushBusy(true);
    try {
      const reg = await swReady();
      if (!reg) { setPushOn(false); setPushMsg({ ok: false, text: '이 환경에선 푸시 알림을 사용할 수 없습니다(인증서/브라우저 미지원).' }); return; }
      const sub = await reg.pushManager.getSubscription();
      await pushAPI.unsubscribe(sub ? sub.endpoint : '');
      if (sub) { try { await sub.unsubscribe(); } catch (e) { /* */ } }
      setPushOn(false);
      setPushMsg({ ok: true, text: '알림이 꺼졌습니다.' });
    } catch (e) { setPushMsg({ ok: false, text: '알림 끄기 실패' }); } finally {
      setPushBusy(false);
      notePushChanged();   // 성공·실패·SW 없음 무관 — 값을 단정하지 않고 다시 읽는다
    }
  };
  // 폰 메신저 알림 지연 저장 — 실패하면 원래 값으로 되돌린다.
  const changeDelay = async (sec) => {
    if (!delayPrefs || delayBusy || sec === delayPrefs.value) return;
    const prev = delayPrefs.value;
    setDelayMsg(null);
    setDelayPrefs((p) => ({ ...p, value: sec }));
    setDelayBusy(true);
    try {
      if (!pushAPI || typeof pushAPI.setPrefs !== 'function') throw new Error('NO_PREFS_API');
      const r = await pushAPI.setPrefs(sec);
      const saved = toDelaySec(r && r.data && r.data.mobile_delay_sec);
      if (saved !== null) setDelayPrefs((p) => ({ ...p, value: saved }));
      setDelayMsg({ ok: true, text: '저장했습니다.' });
    } catch (e) {
      setDelayPrefs((p) => ({ ...p, value: prev }));
      setDelayMsg({ ok: false, text: '알림 지연을 저장하지 못했습니다. 잠시 뒤 다시 시도해 주세요.' });
    } finally {
      setDelayBusy(false);
    }
  };


  return (
    <AppShell app="my" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <Page>
      <PageHeader icon={IconUser} tone="orange" title="내 정보" />

      {/* 입력·값 칸이 한 줄짜리라 폭은 540 그대로 — 머리와 같은 왼쪽 선에 붙인다 */}
      <div style={{ maxWidth: '540px' }}>

        {/* 기본 정보 */}
        <Card title="기본 정보" compact={mobile}>
          <Field label="이메일">
            <div style={{ padding: '7px 10px', background: 'var(--gw-subtle, #fbfbfa)', borderRadius: RADIUS.md, fontSize: '13px', color: 'var(--gw-text, #32312e)', wordBreak: 'break-all' }}>{profile.email}</div>
          </Field>
          <Field label="이름">
            {nameMode ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
                <Input value={nameEdit} onChange={(e) => setNameEdit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setNameMode(false); }}
                  autoFocus style={{ flex: '1 1 180px' }} />
                <Button variant="primary" aria-label="저장" onClick={saveName} style={{ flex: mobile ? '1 1 80px' : undefined }}>저장</Button>
                <Button variant="secondary" aria-label="취소" onClick={() => { setNameMode(false); setNameEdit(profile.name); }}
                  style={{ flex: mobile ? '1 1 80px' : undefined }}>취소</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ padding: '7px 10px', background: 'var(--gw-subtle, #fbfbfa)', borderRadius: RADIUS.md, fontSize: '13px', color: 'var(--gw-text, #32312e)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{profile.name}</div>
                <Button variant="secondary" aria-label="수정" onClick={() => setNameMode(true)}>수정</Button>
              </div>
            )}
          </Field>
          {nameMsg && (
            <div style={{ fontSize: '13px', color: nameMsg.ok ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-red-ink, #a4564d)', marginTop: '-6px' }}>{nameMsg.text}</div>
          )}
          <Field label="그룹">
            <div style={{ padding: '7px 10px', background: 'var(--gw-subtle, #fbfbfa)', borderRadius: RADIUS.md, fontSize: '13px', color: 'var(--gw-text, #32312e)' }}>
              {profile.group_name || '미배정'}
            </div>
          </Field>
          <Field label="권한">
            <div style={{ padding: '7px 10px', background: 'var(--gw-subtle, #fbfbfa)', borderRadius: RADIUS.md, fontSize: '13px', color: 'var(--gw-text, #32312e)' }}>
              {profile.role === 'admin' ? '관리자' : '일반 사용자'}
            </div>
          </Field>
          <Field label="가입일">
            <div style={{ padding: '7px 10px', background: 'var(--gw-subtle, #fbfbfa)', borderRadius: RADIUS.md, fontSize: '13px', color: 'var(--gw-text, #32312e)' }}>
              {fmtDate(profile.created_at)}
            </div>
          </Field>
        </Card>

        {/* 이번 달 토큰 사용량 — 80% 이상 글자는 막대용 중간색 대신 진한 글자색(ink) */}
        <Card title="이번 달 토큰 사용량" compact={mobile}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '8px', gap: '8px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
            <span>{used.toLocaleString()} 토큰 사용{isShared && <span style={{ color: 'var(--gw-muted, #92918d)' }}> · 그룹 공유</span>}</span>
            <span style={{ color: pct >= 100 ? 'var(--gw-red-ink, #a4564d)' : pct >= 80 ? 'var(--gw-yellow-ink, #8d6d28)' : 'var(--gw-text, #32312e)', fontWeight: '600', whiteSpace: 'nowrap' }}>
              {limit > 0 ? `${pct}% · 한도 ${limit.toLocaleString()}` : '한도 없음'}
            </span>
          </div>
          {limit > 0 && (
            <div style={{ background: 'var(--gw-gray-soft, #f1f1ef)', borderRadius: '99px', height: '8px', overflow: 'hidden', display: 'flex' }}>
              {isShared ? (
                <>
                  <div style={{ width: `${myW}%`, background: 'var(--gw-blue, #2383e2)', height: '100%', transition: 'width .3s' }} />
                  <div style={{ width: `${grW}%`, background: 'var(--gw-yellow, #c99630)', height: '100%', transition: 'width .3s' }} />
                </>
              ) : (
                <div style={{ width: `${Math.min(pct, 100)}%`, background: barColor, height: '100%', borderRadius: '99px', transition: 'width .3s' }} />
              )}
            </div>
          )}
          {isShared && limit > 0 && (
            <div style={{ display: 'flex', gap: mobile ? '10px' : '24px', marginTop: '10px', fontSize: '12px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--gw-blue, #2383e2)', fontWeight: '600' }}><span style={{ display: 'inline-block', width: '9px', height: '9px', background: 'var(--gw-blue, #2383e2)', borderRadius: '2px', marginRight: '5px' }} />나 {myUsed.toLocaleString()} ({myPct}%)</span>
              <span style={{ color: 'var(--gw-yellow-ink, #8d6d28)', fontWeight: '600' }}><span style={{ display: 'inline-block', width: '9px', height: '9px', background: 'var(--gw-yellow, #c99630)', borderRadius: '2px', marginRight: '5px' }} />그룹원 {groupUsed.toLocaleString()} ({groupPct}%)</span>
            </div>
          )}
        </Card>

        {/* 이번 달 웹 검색 사용량 */}
        <Card title="이번 달 웹 검색 사용량" compact={mobile}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '8px', gap: '8px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
            <span>{wsUsed.toLocaleString()}회 사용{wsShared && <span style={{ color: 'var(--gw-muted, #92918d)' }}> · 그룹 공유</span>}</span>
            <span style={{ color: wsPct >= 100 ? 'var(--gw-red-ink, #a4564d)' : wsPct >= 80 ? 'var(--gw-yellow-ink, #8d6d28)' : 'var(--gw-text, #32312e)', fontWeight: '600', whiteSpace: 'nowrap' }}>
              {wsLimit > 0 ? `${wsPct}% · 한도 ${wsLimit.toLocaleString()}회` : '한도 없음'}
            </span>
          </div>
          {wsLimit > 0 && (
            <div style={{ background: 'var(--gw-gray-soft, #f1f1ef)', borderRadius: '99px', height: '8px', overflow: 'hidden', display: 'flex' }}>
              {wsShared ? (
                <>
                  <div style={{ width: `${wsMyW}%`, background: 'var(--gw-green, #539b77)', height: '100%', transition: 'width .3s' }} />
                  <div style={{ width: `${wsGrW}%`, background: 'var(--gw-yellow, #c99630)', height: '100%', transition: 'width .3s' }} />
                </>
              ) : (
                <div style={{ width: `${Math.min(wsPct, 100)}%`, background: wsColor, height: '100%', borderRadius: '99px', transition: 'width .3s' }} />
              )}
            </div>
          )}
          {wsShared && wsLimit > 0 && (
            <div style={{ display: 'flex', gap: mobile ? '10px' : '24px', marginTop: '10px', fontSize: '12px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--gw-green-ink, #426b55)', fontWeight: '600' }}><span style={{ display: 'inline-block', width: '9px', height: '9px', background: 'var(--gw-green, #539b77)', borderRadius: '2px', marginRight: '5px' }} />나 {wsMyUsed.toLocaleString()} ({wsMyPct}%)</span>
              <span style={{ color: 'var(--gw-yellow-ink, #8d6d28)', fontWeight: '600' }}><span style={{ display: 'inline-block', width: '9px', height: '9px', background: 'var(--gw-yellow, #c99630)', borderRadius: '2px', marginRight: '5px' }} />그룹원 {wsGroupUsed.toLocaleString()} ({wsGroupPct}%)</span>
            </div>
          )}
        </Card>

        {/* 알림(Web Push) */}
        <Card title="화면 폰트" compact={mobile}>
          <FontPicker value={profile.ui_font} mobile={mobile}
            onSaved={(v) => setProfile((pr) => ({ ...pr, ui_font: v }))} />
        </Card>

        <Card title="알림 (모바일·PC 푸시)" compact={mobile}>
          <p style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '12px', lineHeight: 1.6 }}>
            브라우저 푸시 알림을 켜면 메신저 DM·멘션과 켜 둔 모닝 브리핑 등을 받습니다.
            <br />문의 답변·한도 처리·공유·운영 알림은 사이드바의 <b>알림</b>에서 확인합니다.
            <br />iOS는 Safari에서 <b>'홈 화면에 추가'</b>로 앱을 설치한 뒤 켜야 합니다.
          </p>
          {pushMsg && <div style={{ fontSize: '13px', color: pushMsg.ok ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-red-ink, #a4564d)', marginBottom: '10px' }}>{pushMsg.text}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: pushOn ? 'var(--gw-green-soft, #dcebe2)' : 'var(--gw-gray-soft, #f1f1ef)', borderRadius: RADIUS.lg, border: 'none', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: 600, fontSize: '14px', color: pushOn ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-secondary, #64635f)' }}>
              <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: pushOn ? 'var(--gw-green, #539b77)' : 'var(--gw-muted, #92918d)', display: 'inline-block' }} />
              {pushOn ? '알림 켜짐' : '알림 꺼짐'}
            </span>
            <Button variant={pushOn ? 'danger' : 'primary'} onClick={pushOn ? disablePush : enablePush} disabled={pushBusy} aria-label={pushOn ? '푸시 알림 끄기' : '푸시 알림 켜기'}
              style={{ marginLeft: mobile ? 0 : 'auto', width: mobile ? '100%' : undefined }}>
              {pushBusy ? '처리 중…' : (pushOn ? '알림 끄기' : '알림 켜기')}
            </Button>
          </div>
          {delayPrefs && (
            <div style={{ marginTop: '14px' }}>
              <label htmlFor="mm-mobile-delay" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--gw-text, #32312e)', marginBottom: '6px' }}>
                PC 를 쓰는 중일 때 폰 메신저 알림
              </label>
              <select id="mm-mobile-delay" value={String(delayPrefs.value)} disabled={delayBusy}
                onChange={(e) => changeDelay(Number(e.target.value))}
                style={{ ...inputBase, minHeight: '31px', background: delayBusy ? 'var(--gw-gray-soft, #f1f1ef)' : 'var(--gw-surface, #fff)' }}>
                {delayPrefs.choices.map((sec) => (
                  <option key={sec} value={String(sec)}>{delayLabel(sec, delayPrefs.def)}</option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', margin: '6px 0 0', lineHeight: 1.6 }}>
                게이트웨이를 쓰고 있는 기기에는 바로 알리고, 다른 기기에는 이 시간만큼 기다렸다가 그 사이 읽지 않았을 때만 보냅니다.
              </p>
              {delayMsg && <div style={{ fontSize: '13px', color: delayMsg.ok ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-red-ink, #a4564d)', marginTop: '6px' }}>{delayMsg.text}</div>}
            </div>
          )}
        </Card>

        {/* 메일 AI 지침 — 메일 메뉴(mail2) 권한이 없으면 카드가 스스로 안 그린다. 팝업 「지침 설정」이 /my#mail-ai 로 온다. */}
        <MailAiGuideCard user={profile} mobile={mobile} />

        {/* 내 데이터가 얼마나 남는지 — 본인에게 알려야 할 사실이라 화면에 적어 둔다.
            (계정 삭제 시 3년 보존은 개인정보 보존기간에 해당한다) */}
        <Card title="내 데이터 보관 안내" compact={mobile}>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--gw-secondary, #64635f)', lineHeight: 1.9 }}>
            <li>대화는 <b>직접 삭제하기 전까지</b> 보관됩니다. 다만 보관 중인 대화가 50개가 된 상태에서
              <b>새 대화를 시작하면</b> 확인을 거쳐 오래된 것부터 삭제됩니다(보관함에 넣은 대화는 이 수에 세지 않습니다).</li>
            <li>채팅에 올린 <b>첨부 파일은 30일</b> 후 서버에서 삭제되어 더는 내려받을 수 없습니다.</li>
            <li>계정이 삭제되면 첨부 파일은 함께 삭제되고, <b>대화 본문은 3년간 보존</b>됩니다 —
              인수인계·사고 조사 목적이며 <b>시스템 관리자만</b> 열람할 수 있고 <b>열람 기록이 남습니다</b>.
              3년이 지나면 자동으로 파기됩니다.</li>
            <li>사용량·비용 기록은 조직 장부라 계정 삭제 후에도 남습니다(대화 내용은 포함되지 않습니다).</li>
          </ul>
        </Card>

        {/* 비밀번호 변경 */}
        <Card title="비밀번호 변경" compact={mobile}>
          <Field label="현재 비밀번호">
            <Input type="password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} placeholder="현재 비밀번호" aria-label="현재 비밀번호" />
          </Field>
          <Field label="새 비밀번호">
            <Input type="password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} placeholder="8자 이상" aria-label="새 비밀번호" />
          </Field>
          <Field label="새 비밀번호 확인">
            <Input type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              placeholder="새 비밀번호를 다시 입력" aria-label="새 비밀번호 확인"
              onKeyDown={(e) => e.key === 'Enter' && changePassword()} />
          </Field>
          {pwMsg && (
            <div style={{ fontSize: '13px', color: pwMsg.ok ? 'var(--gw-green-ink, #426b55)' : 'var(--gw-red-ink, #a4564d)', marginBottom: '10px' }}>{pwMsg.text}</div>
          )}
          <Button variant="primary" onClick={changePassword} disabled={loading} aria-label="비밀번호 변경" style={{ width: '100%' }}>
            {loading ? '변경 중...' : '비밀번호 변경'}
          </Button>
        </Card>

      </div>
      </Page>
      </div>
    </AppShell>
  );
}
